<template>
  <v-container fluid class="fill-height">
    <v-row align="center" justify="center" no-gutters>
      <v-col cols="12" sm="9" md="6" lg="4">
        <v-card class="elevation-12">
          <!-- language selection -->
          <div class="text-right pr-2 pt-2">
            <LanguageSelector />
          </div>

          <!-- Logo -->
          <v-img
            :src="require('@/assets/LTOU_logo.png')"
            height="40"
            width="300"
            class="ma-auto"
            contain
          />

          <v-form ref="form" autocomplete="off" @submit.prevent="submit">
            <v-card-text>
              <h3 class="mb-4">{{ $t('reset_password') }}</h3>

              <!-- email -->
              <v-text-field
                v-model="email"
                :label="$t('username')"
                prepend-icon="mdi-email"
                type="email"
                autocomplete="off"
                readonly
              />

              <!-- new password -->
              <PasswordTextField
                v-model="password"
                :label="$t('new_password')"
                :error-messages="toArray(errors.password)"
                @input="$delete(errors, 'password')"
              />

              <!-- confirmation -->
              <v-text-field
                v-model="password_confirmation"
                :label="$t('confirm_password')"
                :type="showConfirm ? 'text' : 'password'"
                :append-icon="showConfirm ? 'mdi-eye-off' : 'mdi-eye'"
                :error-messages="toArray(errors.password_confirmation)"
                @click:append="showConfirm = !showConfirm"
                @input="$delete(errors, 'password_confirmation')"
                autocomplete="off"
              />

              <!-- 2FA dialog -->
              <TwoFactorCodeDialog
                v-model="otpDialogOpen"
                :title="$t('two_factor.login_title')"
                :subtitle="$t('two_factor.login_subtitle')"
                :error="otpDialogError"
                @confirm="onOtpConfirm"
                @cancel="onOtpCancel"
              />
            </v-card-text>

            <v-card-actions class="px-4 pb-4">
              <v-btn
                color="primary"
                type="submit"
                :loading="loading"
                :disabled="loading"
                class="mr-2"
                block
              >
                {{ $t('change_password') }}
              </v-btn>
            </v-card-actions>

            <v-card-actions class="px-4 pb-6">
              <v-btn
                outlined
                color="primary"
                :to="{ name: 'login' }"
                :disabled="loading"
                block
              >
                {{ $t('back_to_login') }}
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import LanguageSelector from '@/components/LanguageSelector'
import PasswordTextField from '@/components/PasswordTextField'
import httpClient from '@/api/http-client'
import authService from '@/api/auth-service';
import { mapErrorsToInputs } from '@/util/forms'
import eventBus, { OPEN_SNACKBAR } from '@/util/event-bus'
import TwoFactorCodeDialog from '@/components/TwoFactorCodeDialog.vue'

export default {
  name: 'ResetPassword',
  components: { LanguageSelector, PasswordTextField, TwoFactorCodeDialog },
  data: () => ({
    token: '',
    email: '',
    password: '',
    password_confirmation: '',
    otp_code: '',
    loading: false,
    showConfirm: false,
    errors: {},

    // 2FA dialog state
    otpDialogOpen: false,
    otpDialogError: '',
    pendingPayload: null, // keep last payload for resending
  }),
  created() {
    this.token = this.$route.query.token || ''
    this.email = this.$route.query.email || ''

    if (!this.token || !this.email) {
      eventBus.$emit(OPEN_SNACKBAR, this.$t('invalid_reset_link'))
    }
  },
  methods: {
    toArray(val) {
      if (Array.isArray(val)) return val
      if (val == null || val === '') return []
      return [String(val)]
    },
    async submit() {
      if (!this.token || !this.email) return
      this.loading = true
      this.errors = {}
      this.otpDialogError = ''

      // First attempt without 2FA code
      const payload = {
        token: this.token,
        email: this.email,
        password: this.password,
        password_confirmation: this.password_confirmation,
      }

      this.pendingPayload = payload

      try {
        const res = await authService.resetPassword(payload);

        // success
        if (res.data?.status === 'reset') {
          await this.$store.dispatch('auth/logout', { silent: true }).catch(() => {})
          delete httpClient.defaults.headers.common.Authorization
          window.location.replace('/login')
          return
        }

        // unexpected_response
        eventBus.$emit(OPEN_SNACKBAR, this.$t('reset_failed_try_again'))

      } catch (e) {
        const status = e?.response?.status
        const data = e?.response?.data || {}

        // 2FA setup required (user has 2FA enabled but does not have a generated secret)
        if (status === 428 && data.message === 'two_factor.setup_required') {
          eventBus.$emit(OPEN_SNACKBAR, this.$t('two_factor.setup_required'))
          this.$router.push({ name: 'profile' })
          return
        }

        // 2FA – need code or code invalid → open dialog
        if (status === 422) {
          // OTP/recovery required
          if (data.message === 'two_factor.otp_code_required') {
            this.otpDialogOpen = true
            return
          }

          // OTP/recovery invalid
          if (
            data.message === 'two_factor.otp_code_invalid' ||
            data.message === 'two_factor.recovery_code_invalid'
          ) {
            this.otpDialogOpen = true
            this.otpDialogError = this.$t(data.message)
            return
          }

          eventBus.$emit(OPEN_SNACKBAR, this.$t(data.message))
          return
        }

        // FormRequest errors
        if (status === 422) {
          this.errors = mapErrorsToInputs(e)
          return
        }

        // Common error
        eventBus.$emit(OPEN_SNACKBAR, data?.message || this.$t('reset_failed_try_again'))

      } finally {
        this.loading = false
      }
    },

    // with 2FA
    async onOtpConfirm(codePayload) {
      if (!this.pendingPayload) return
      this.loading = true
      this.otpDialogError = ''

      try {
        const res = await authService.resetPassword({
          ...this.pendingPayload,
          ...codePayload, // { otp_code } arba { recovery_code }
        })

        if (res.data?.status === 'reset') {
          await this.$store.dispatch('auth/logout', { silent: true }).catch(() => {})
          delete httpClient.defaults.headers.common.Authorization
          window.location.replace('/login')
          return
        }

        eventBus.$emit(OPEN_SNACKBAR, this.$t('reset_failed_try_again'))

      } catch (e) {
        const status = e?.response?.status
        const data = e?.response?.data || {}

        // Invalid code - keep dialog open and show error inside
        if (
          status === 422 &&
          (data.message === 'two_factor.otp_code_invalid' || data.message === 'two_factor.recovery_code_invalid')
        ) {
          this.otpDialogOpen = true
          this.otpDialogError = this.$t(data.message)
          return
        }

        // 2FA setup required (user has 2FA enabled but does not have a generated secret)
        if (status === 428 && data.message === 'two_factor.setup_required') {
          this.otpDialogOpen = false
          eventBus.$emit(OPEN_SNACKBAR, this.$t('two_factor.setup_required'))
          this.$router.push({ name: 'profile' })
          return
        }

        eventBus.$emit(OPEN_SNACKBAR, data?.message || this.$t('reset_failed_try_again'))

      } finally {
        this.loading = false
      }
    },

    onOtpCancel() {
      this.otpDialogOpen = false
      this.otpDialogError = ''
    },
  },
}
</script>